import React, { useState, useEffect } from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Typography, Box, Chip, Popover, List, 
  ListItemButton, ListItemText, Switch, 
  FormControlLabel, Divider, ListSubheader
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Tooltip from '@mui/material/Tooltip';


export interface FilterCriteria {
  verified: boolean;
  veteran: boolean; 
  ethnicity: string | null;
  religion: string | null;
  location: string | null;
  politicalView: string | null;
  topic: string;
}

interface FilterComponentProps {
  onFilterChange: (criteria: FilterCriteria) => void;
}

interface FilterComponentProps {
  onFilterChange: (criteria: FilterCriteria) => void;
  inCall: boolean; 
}

export const buildFilterSentence = (criteria: FilterCriteria): string => {
  let parts = [];
  if (criteria.verified) parts.push('Verified');
  if (criteria.veteran) parts.push('Veteran'); 
  if (criteria.ethnicity) parts.push(criteria.ethnicity);
  if (criteria.religion) parts.push(criteria.religion);
  
  // Convert state names to demonyms
  if (criteria.location) {
    const isState = Object.keys(stateToDemonym).includes(criteria.location);
    parts.push(isState ? stateToDemonym[criteria.location as keyof typeof stateToDemonym] : criteria.location);
  }
  
  if (criteria.politicalView) parts.push(criteria.politicalView);

  if (parts.length === 0) return "Call random American";
  return `Call random ${parts.join(', ')} American`;
};

export const getDefaultFilterCriteria = (): FilterCriteria => ({
  verified: false,
  veteran: false, 
  ethnicity: null,
  religion: null,
  location: null,
  politicalView: null,
  topic: 'Politics'
});

const ethnicityOptions = [
  'White', 'Black', 'Hispanic', 'Asian', 'Native American', 'Pacific Islander', 'Middle Eastern'
];

const religionOptions = [
  'Christian', 'Catholic', 'Jewish', 'Muslim', 'Hindu', 'Buddhist', 'Atheist', 'Agnostic'
];

const regionOptions = [
  'Midwest', 'Northeast',  'West', 'South' /*'Southwest', 'Southeast', 'Northwest', 'Midatlantic',
  'Rural', 'Urban', 'Suburban'*/
];

// Map of states to their demonyms (resident names)
const stateToDemonym: { [key: string]: string } = {
  /*'Alabama': 'Alabamian',
  'Alaska': 'Alaskan',
  'Arizona': 'Arizonan',
  'Arkansas': 'Arkansan',
  'California': 'Californian',
  'Colorado': 'Coloradan',
  'Connecticut': 'Connecticuter',
  'Delaware': 'Delawarean',
  'Florida': 'Floridian',
  'Georgia': 'Georgian',
  'Hawaii': 'Hawaiian',
  'Idaho': 'Idahoan',
  'Illinois': 'Illinoisan',
  'Indiana': 'Hoosier',
  'Iowa': 'Iowan',
  'Kansas': 'Kansan',
  'Kentucky': 'Kentuckian',
  'Louisiana': 'Louisianan',
  'Maine': 'Mainer',
  'Maryland': 'Marylander',
  'Massachusetts': 'Massachusettsan',
  'Michigan': 'Michigander',
  'Minnesota': 'Minnesotan',
  'Mississippi': 'Mississippian',
  'Missouri': 'Missourian',
  'Montana': 'Montanan',
  'Nebraska': 'Nebraskan',
  'Nevada': 'Nevadan',
  'New Hampshire': 'New Hampshirite',
  'New Jersey': 'New Jerseyan',
  'New Mexico': 'New Mexican',
  'New York': 'New Yorker',
  'North Carolina': 'North Carolinian',
  'North Dakota': 'North Dakotan',
  'Ohio': 'Ohioan',
  'Oklahoma': 'Oklahoman',
  'Oregon': 'Oregonian',
  'Pennsylvania': 'Pennsylvanian',
  'Rhode Island': 'Rhode Islander',
  'South Carolina': 'South Carolinian',
  'South Dakota': 'South Dakotan',
  'Tennessee': 'Tennessean',
  'Texas': 'Texan',
  'Utah': 'Utahn',
  'Vermont': 'Vermonter',
  'Virginia': 'Virginian',
  'Washington': 'Washingtonian',
  'West Virginia': 'West Virginian',
  'Wisconsin': 'Wisconsinite',
  'Wyoming': 'Wyomingite'*/
};

const politicalViewOptions = [
  //'Liberal', 'Conservative', 'Moderate', 'Progressive', 'Libertarian'
  'Republican', 'Democrat', 'Independent', 'Green', 'Libertarian'
];

const topicOptions = [
  'Politics', 'Healthcare', 'Economy', 'Immigration', /*'Climate Change',*/ 
  'Gun Control', 'Education', /*'Social Issues',*/ 'Foreign Policy'
];

const FilterDialog = ({ open, onClose, criteria, onChange }: {
  open: boolean;
  onClose: () => void;
  criteria: FilterCriteria;
  onChange: (criteria: FilterCriteria) => void;
}) => {
  const [localCriteria, setLocalCriteria] = useState<FilterCriteria>(criteria);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeField, setActiveField] = useState<keyof FilterCriteria | null>(null);
  
  // Update local criteria when props change
  useEffect(() => {
    setLocalCriteria(criteria);
  }, [criteria]);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, field: keyof FilterCriteria) => {
    setAnchorEl(event.currentTarget);
    setActiveField(field);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setActiveField(null);
  };

  const handleOptionSelect = (value: string | boolean) => {
    if (activeField) {
      setLocalCriteria(prev => ({
        ...prev,
        [activeField]: activeField === 'verified' || activeField === 'veteran' ? value : 
                     prev[activeField] === value ? null : value
      }));
    }
  };

  const handleReset = () => {
    setLocalCriteria(getDefaultFilterCriteria());
  };

  const handleApply = () => {
    onChange(localCriteria);
    onClose();
  };

  const popoverOpen = Boolean(anchorEl);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: { 
          borderRadius: 2,
          border: '1px solid #ccc'
        }
      }}
    >
      <DialogTitle sx={{ 
        backgroundColor: '#002a5a', 
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon />
          <Typography variant="h6">Find Specific Discussion Partners</Typography>
        </Box>
        <Button 
          onClick={onClose} 
          sx={{ color: 'white', minWidth: 'auto' }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      
      <DialogContent sx={{ py: 4 }}>

        <Box sx={{ 
          backgroundColor: '#f8f8f8', 
          p: 2, 
          mb: 4,
          borderRadius: 2,
          border: '1px dashed #ccc' 
        }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontStyle: 'italic' }}>
            {buildFilterSentence(localCriteria)} to discuss {localCriteria.topic.toLowerCase()}...
          </Typography>
        </Box>
        
        {/* Interactive Sentence Builder */}
        <Typography variant="h6" sx={{ mb: 2 }}>Build Your Search</Typography>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            mb: 4, 
            flexWrap: 'wrap',
            p: 3,
            borderRadius: 2,
            backgroundColor: '#f5f5f5',
            border: '1px solid #ddd'
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            <span>Call random </span>
            
            {/* Verified Toggle */}
            {/*<Box 
              component="span" 
              onClick={(e) => handlePopoverOpen(e, 'verified')}
              sx={{ 
                cursor: 'pointer', 
                mx: 0.5,
                color: localCriteria.verified ? '#002a5a' : 'gray',
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                fontWeight: localCriteria.verified ? 700 : 400,
                '&:hover': { opacity: 0.8 }
              }}
            >
              {localCriteria.verified ? 'Verified' : 'Verified?'} <ExpandMoreIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
            </Box>*/}

            {/* Veteran Toggle */}
            {/*<Box 
              component="span" 
              onClick={(e) => handlePopoverOpen(e, 'veteran')}
              sx={{ 
                cursor: 'pointer', 
                mx: 0.5,
                color: localCriteria.veteran ? '#002a5a' : 'gray',
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                fontWeight: localCriteria.veteran ? 700 : 400,
                '&:hover': { opacity: 0.8 }
              }}
            >
              {localCriteria.veteran ? 'Veteran' : 'Veteran?'} <ExpandMoreIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
            </Box>*/}

            {/* Ethnicity */}
            <Tooltip title="Ethnicity filtering is currently disabled" arrow>
                <Box 
                component="span" 
                //onClick={(e) => handlePopoverOpen(e, 'ethnicity')}
                sx={{ 
                  cursor: 'not-allowed', 
                  opacity: 0.6,
                  color: 'gray',
                  //cursor: 'pointer',
                  //color: localCriteria.ethnicity ? '#002a5a' : 'gray',
                  mx: 0.5,
                  textDecoration: 'underline',
                  textDecorationStyle: 'dotted',
                  fontWeight: localCriteria.ethnicity ? 700 : 400,
                  '&:hover': { opacity: 0.8 }
                }}
              >
                {localCriteria.ethnicity || 'Ethnicity'} <ExpandMoreIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
              </Box>
            </Tooltip>

            {/* Religion */}
            <Tooltip title="Religion filtering is currently disabled" arrow>
            <Box 
              component="span" 
              //onClick={(e) => handlePopoverOpen(e, 'religion')}
              sx={{ 
                cursor: 'not-allowed', 
                opacity: 0.6,
                color: 'gray',
                //cursor: 'pointer', 
                //color: localCriteria.religion ? '#002a5a' : 'gray',
                mx: 0.5,
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                fontWeight: localCriteria.religion ? 700 : 400,
                '&:hover': { opacity: 0.8 }
              }}
            >
              {localCriteria.religion || 'Religion'} <ExpandMoreIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
            </Box>
            </Tooltip>

            {/* Location */}
            <Box 
              component="span" 
              onClick={(e) => handlePopoverOpen(e, 'location')}
              sx={{ 
                cursor: 'pointer', 
                mx: 0.5,
                color: localCriteria.location ? '#002a5a' : 'gray',
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                fontWeight: localCriteria.location ? 700 : 400,
                '&:hover': { opacity: 0.8 }
              }}
            >
              {localCriteria.location || 'Location'} <ExpandMoreIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
            </Box>

            {/* Political View */}
            <Tooltip title="Political View filtering is currently disabled" arrow>
            <Box 
              component="span" 
              //onClick={(e) => handlePopoverOpen(e, 'politicalView')}
              sx={{ 
                cursor: 'not-allowed', 
                opacity: 0.6,
                color: 'gray',
                //cursor: 'pointer', 
                //color: localCriteria.politicalView ? '#002a5a' : 'gray',
                mx: 0.5,
                textDecoration: 'underline',
                textDecorationStyle: 'dotted',
                fontWeight: localCriteria.politicalView ? 700 : 400,
                '&:hover': { opacity: 0.8 }
              }}
            >
              {localCriteria.politicalView || 'Political View'} <ExpandMoreIcon fontSize="small" sx={{ verticalAlign: 'middle' }} />
            </Box>
            </Tooltip>
            <span>American</span>
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />
        
        {/* Discussion Topic */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Discussion Topic</Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {topicOptions.map((topic) => (
              <Chip
                key={topic}
                label={topic}
                onClick={() => setLocalCriteria(prev => ({...prev, topic}))}
                sx={{
                  backgroundColor: localCriteria.topic === topic ? '#002a5a' : '#e0e0e0',
                  color: localCriteria.topic === topic ? 'white' : 'black',
                  '&:hover': {
                    backgroundColor: localCriteria.topic === topic ? '#001a3a' : '#d0d0d0',
                  }
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Note about match waiting times */}
        <Box sx={{ 
          mt: 3,
          p: 2, 
          borderRadius: 2,
          backgroundColor: '#fff8e6',
          border: '1px solid #ffe0b2' 
        }}>
          <Typography variant="caption" sx={{ display: 'block', color: '#b36a00' }}>
            <strong>Note:</strong> More specific criteria may result in longer wait times for matching. Options may be disabled due to capacity.
          </Typography>
        </Box>
      </DialogContent>
      
      {/* Selection Popover */}
      <Popover
        open={popoverOpen}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: { 
            p: 2,
            maxHeight: '300px',
            width: activeField === 'verified' || activeField === 'veteran' ? '200px' : '300px'
          }
        }}
      >
        {activeField === 'verified' ? (
          <FormControlLabel
            control={
              <Switch 
                checked={localCriteria.verified}
                onChange={(e) => handleOptionSelect(e.target.checked)}
                color="primary"
              />
            }
            label="Only verified users"
          />
        ) : activeField === 'veteran' ? (
          <FormControlLabel
            control={
              <Switch 
                checked={localCriteria.veteran}
                onChange={(e) => handleOptionSelect(e.target.checked)}
                color="primary"
              />
            }
            label="Only veteran users"
          />
        ) : activeField === 'ethnicity' ? (
          <List dense>
            {ethnicityOptions.map((option) => (
              <ListItemButton
                key={option} 
                onClick={() => handleOptionSelect(option)}
                selected={localCriteria.ethnicity === option}
              >
                <ListItemText primary={option} />
              </ListItemButton>
            ))}
          </List>
        ) : activeField === 'religion' ? (
          <List dense>
            {religionOptions.map((option) => (
              <ListItemButton
                key={option}
                onClick={() => handleOptionSelect(option)}
                selected={localCriteria.religion === option}
              >
                <ListItemText primary={option} />
              </ListItemButton>
            ))}
          </List>
        ) : activeField === 'location' ? (
          <List dense>
            <ListSubheader>Regions</ListSubheader>
            {regionOptions.map((option) => (
              <ListItemButton
                key={option}
                onClick={() => handleOptionSelect(option)}
                selected={localCriteria.location === option}
              >
                <ListItemText primary={option} />
              </ListItemButton>
            ))}
            
            <Divider sx={{ my: 1 }} />
            <ListSubheader>States</ListSubheader>
            
            {Object.keys(stateToDemonym).map((state) => (
              <ListItemButton
                key={state}
                onClick={() => handleOptionSelect(state)}
                selected={localCriteria.location === state}
              >
                <ListItemText 
                  primary={state} 
                  secondary={stateToDemonym[state as keyof typeof stateToDemonym]}
                />
              </ListItemButton>
            ))}
          </List>
        ) : activeField === 'politicalView' ? (
          <List dense>
            {politicalViewOptions.map((option) => (
              <ListItemButton
                key={option}
                onClick={() => handleOptionSelect(option)}
                selected={localCriteria.politicalView === option}
              >
                <ListItemText primary={option} />
              </ListItemButton>
            ))}
          </List>
        ) : null}
      </Popover>
      
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button 
          onClick={handleReset} 
          variant="outlined" 
          color="primary"
        >
          Reset All
        </Button>
        <Button 
          onClick={handleApply} 
          variant="contained" 
          sx={{ 
            backgroundColor: '#002a5a',
            '&:hover': { backgroundColor: '#001a3a' },
          }}
          startIcon={<FilterListIcon />}
        >
          Apply Filters
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main SessionFilter component that encapsulates all the filtering functionality
const SessionFilter: React.FC<FilterComponentProps> = ({ onFilterChange,inCall }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [criteria, setCriteria] = useState<FilterCriteria>(getDefaultFilterCriteria());

  const handleOpenFilter = () => {
    setIsOpen(true);
  };

  const handleCloseFilter = () => {
    setIsOpen(false);
  };

  const handleCriteriaChange = (newCriteria: FilterCriteria) => {
    setCriteria(newCriteria);
    onFilterChange(newCriteria);
  };

  return (
    <>
      {/* Top box with adjust button */}
    <Box
      sx={{
        backgroundColor: "#EEF1F2",
        p: { xs: 2, sm: 1 }, // More padding on mobile
        borderRadius: 10,
        display: 'flex',
        flexDirection: { /*xs: 'column',*/ sm: 'row' },
        alignItems: { xs: 'flex-start', sm: 'center' },
        width: '96%',
        margin: 'auto',
        boxSizing: 'border-box', // Important for proper sizing
      }}
    >
      {<Button 
        variant="contained"
        onClick={handleOpenFilter}
        disabled={inCall} 
        sx={{ 
          backgroundColor: '#B31942',
          borderRadius: 10,
          //width: { xs: '50%', sm: '6rem' }, // Full width on mobile, 6rem on larger screens
          width: '6rem',
          mr: { xs: 1, sm: 1 },
          mb: { xs: 1, sm: 0 },
          //ml: { xs: 1, sm: 0 },
          alignSelf: { xs: 'center', sm: 'unset' }, 
          '&:hover': { backgroundColor: 'darkred' },
          textTransform: 'none',
          flexShrink: 0, // Prevent button from shrinking
        }}
      >
        Adjust
      </Button>}
      <Typography
        sx={{
          fontSize: { xs: '0.9rem', sm: '1rem' }, // Slightly smaller on mobile
          ml: { xs: 0, sm: '1rem' },
          color: '#666',
          wordBreak: 'break-word',
          width: '100%', // Take up full width of parent container
          textAlign: 'left',
          py: { xs: 0.5, sm: 0 }, // Add some vertical padding on mobile
        }}
      > 
        {buildFilterSentence(criteria)} to discuss {criteria.topic.toLowerCase()}...
      </Typography>
    </Box>

      <FilterDialog 
        open={isOpen}
        onClose={handleCloseFilter}
        criteria={criteria}
        onChange={handleCriteriaChange}
      />
    </>
  );
};

export default SessionFilter;